import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import { Save, Trash2 } from "lucide-react";
import { Alert, Button, Card, ErrorState, FormStack, LoadingState, Page } from "@marekl/adminer/components";
import { apiErrorToFormErrors, rootErrorMessage } from "@marekl/adminer/forms";
import { notifySuccess } from "@marekl/adminer/api";
import { useConfirm } from "@marekl/adminer/hooks";
import { UnsavedChangesModal, useSetTopbarTitle, useUnsavedChangesGuard } from "@marekl/adminer/shell";
import { BillingDetailsFields, emptyBillingDetails } from "../../components/BillingDetailsFields";
import { useCurrentCompany } from "../../lib/company";
import { useContact, useDeleteContact, useSaveContact, type Contact, type ContactInput } from "../../lib/queries/contacts";
import styles from "./ContactEditPage.module.css";

interface ContactForm {
  billing_details: ReturnType<typeof emptyBillingDetails>;
}

function toForm(c?: Contact): ContactForm {
  const bd = emptyBillingDetails();
  if (c) {
    for (const k of Object.keys(bd) as (keyof typeof bd)[]) {
      (bd as Record<string, unknown>)[k] = c.billing_details[k] ?? bd[k];
    }
  }
  return { billing_details: bd };
}

function ContactForm({ contact }: { contact?: Contact }) {
  const { id, can } = useCurrentCompany();
  const navigate = useNavigate();
  const save = useSaveContact(id);
  const del = useDeleteContact(id);
  const confirm = useConfirm();
  const form = useForm<ContactForm>({ mode: "onTouched", defaultValues: toForm(contact) });
  const [leaveTo, setLeaveTo] = useState<string>();
  const guard = useUnsavedChangesGuard(form.formState.isDirty && !leaveTo);
  const editor = can("editor");

  useEffect(() => {
    if (leaveTo && !form.formState.isDirty) navigate(leaveTo);
  }, [leaveTo, form.formState.isDirty, navigate]);

  async function onSubmit(v: ContactForm) {
    try {
      await save.mutateAsync({ id: contact?.id, input: v as ContactInput });
      notifySuccess(contact ? "Kontakt byl uložen." : "Kontakt byl přidán.");
      form.reset(v);
      setLeaveTo(`/c/${id}/contacts`);
    } catch (err) {
      apiErrorToFormErrors<ContactForm>(err, form.setError, { fallbackMessage: "Uložení se nepovedlo." });
    }
  }

  return (
    <>
      <FormProvider {...form}>
        <form noValidate onSubmit={form.handleSubmit(onSubmit)}>
          <Card>
            <Card.Header title={contact ? "Úprava kontaktu" : "Nový kontakt"} subtitle={contact?.billing_details.business_name} />
            <Card.Content>
              <FormStack>
                {rootErrorMessage(form.formState.errors) && <Alert variant="err">{rootErrorMessage(form.formState.errors)}</Alert>}
                <fieldset className={styles.fields} disabled={!editor}>
                  <BillingDetailsFields />
                </fieldset>
              </FormStack>
            </Card.Content>
            {editor && (
              <Card.Footer>
                <div className={styles.footer}>
                  {contact && (
                    <Button
                      type="button"
                      variant="danger"
                      weight="ghost"
                      startIcon={<Trash2 size={16} aria-hidden="true" />}
                      onClick={async () => {
                        if (await confirm({ title: "Smazat kontakt", description: "Kontakt zmizí z adresáře; vystavené doklady zůstanou beze změny.", confirmLabel: "Smazat", variant: "danger" })) {
                          await del.mutateAsync(contact.id);
                          setLeaveTo(`/c/${id}/contacts`);
                        }
                      }}
                    >
                      smazat kontakt
                    </Button>
                  )}
                  <span className={styles.spacer} />
                  <Button type="button" variant="primary" weight="outline" onClick={() => navigate(`/c/${id}/contacts`)}>
                    zrušit
                  </Button>
                  <Button type="submit" variant="primary" startIcon={<Save size={16} aria-hidden="true" />} loading={save.isPending}>
                    uložit
                  </Button>
                </div>
              </Card.Footer>
            )}
          </Card>
        </form>
      </FormProvider>
      <UnsavedChangesModal open={guard.blocked} onConfirm={guard.proceed} onClose={guard.cancel} />
    </>
  );
}

/** /c/:companyId/contacts/new and /c/:companyId/contacts/:contactId */
export function ContactEditPage() {
  const { id } = useCurrentCompany();
  const { contactId } = useParams();
  const contact = useContact(id, contactId === "new" ? undefined : contactId);
  useSetTopbarTitle("Adresář");
  return (
    <Page>
      <Page.Content>
        {contactId !== "new" && contact.isLoading ? (
          <LoadingState />
        ) : contact.error ? (
          <ErrorState title="Kontakt se nepodařilo načíst" error={contact.error} onRetry={contact.refetch} />
        ) : (
          <ContactForm key={contact.data?.id ?? "new"} contact={contact.data} />
        )}
      </Page.Content>
    </Page>
  );
}
